import { useMemo, useState } from "react";
import DocenteCard from "./DocenteCard";
import { theme } from "../styles/theme";
import { calcularCarga } from "../utils/docentes";
import { normalizarClaveDocente } from "../utils/docenteNombre";
import { obtenerAnioDeFecha } from "../utils/registroSchema";

const ANIOS_DISPONIBLES = ["2020", "2021", "2022", "2023", "2024", "2025", "2026", "2027", "2028", "2029"];
const FILTROS_ESTADO = [
  { id: "todos", label: "Todos" },
  { id: "GRADUADO", label: "Graduado" },
  { id: "PROPUESTA DE TEMAS Y TEMARIOS", label: "Propuesta de temas y temarios" }
];

const normalizarEstado = (valor) => String(valor ?? "").trim().toUpperCase();
const obtenerAnioRegistro = (item) => String(item?.anio ?? "").trim();
const construirClaveRegistro = (item) =>
  item?._rowNumber ||
  [
    item?.anio,
    item?.maestria,
    item?.cohorte,
    item?.estudiante,
    item?.tema,
    item?.director,
    item?.evaluador
  ].join("|");

const unirRegistros = (...listas) => {
  const registros = new Map();

  listas.flat().forEach((item) => {
    registros.set(construirClaveRegistro(item), item);
  });

  return Array.from(registros.values());
};

function Avances({ data, anioActivo, onChangeAnio, onSelect }) {
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [busquedaDocente, setBusquedaDocente] = useState("");
  const dataAsignados = data.filter((item) => obtenerAnioRegistro(item) === anioActivo);
  const dataGraduados = data.filter(
    (item) =>
      normalizarEstado(item?.estado) === "GRADUADO" &&
      obtenerAnioDeFecha(item?.fecha_graduacion) === anioActivo
  );
  const dataPropuesta = dataAsignados.filter(
    (item) => normalizarEstado(item?.estado) === "PROPUESTA DE TEMAS Y TEMARIOS"
  );
  const dataFiltrada =
    filtroEstado === "GRADUADO"
      ? dataGraduados
      : filtroEstado === "PROPUESTA DE TEMAS Y TEMARIOS"
        ? dataPropuesta
        : unirRegistros(dataAsignados, dataGraduados);
  const docentesOrdenados = calcularCarga(dataFiltrada).sort((a, b) => b.total - a.total);
  const busquedaNormalizada = normalizarClaveDocente(busquedaDocente);
  const docentesFiltrados = useMemo(() => {
    if (!busquedaNormalizada) {
      return docentesOrdenados;
    }

    return docentesOrdenados.filter((docente) =>
      normalizarClaveDocente(docente.nombre).includes(busquedaNormalizada)
    );
  }, [busquedaNormalizada, docentesOrdenados]);
  const recomendaciones = busquedaNormalizada ? docentesFiltrados.slice(0, 6) : [];

  const seleccionarDocenteDesdeBusqueda = (nombre) => {
    setBusquedaDocente(nombre);
    onSelect(nombre);
  };

  const abrirDetalleDocente = (nombre) => {
    onSelect(nombre);
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Histórico de participación docente</h2>
          <p style={styles.subtitle}>
            Revisa el análisis anual de participaciones como Tutor y Evaluador. El detalle y el informe exportado
            respetan el año seleccionado.
          </p>
        </div>

        <div style={styles.controls}>
          <label style={styles.selectorLabel}>
            Año
            <select value={anioActivo} onChange={(event) => onChangeAnio(event.target.value)} style={styles.select}>
              {ANIOS_DISPONIBLES.map((anio) => (
                <option key={anio} value={anio}>
                  {anio}
                </option>
              ))}
            </select>
          </label>

          <div style={styles.filterPanel}>
            {FILTROS_ESTADO.map((filtro) => (
              <button
                key={filtro.id}
                type="button"
                onClick={() => setFiltroEstado(filtro.id)}
                style={{
                  ...styles.filterButton,
                  ...(filtroEstado === filtro.id ? styles.filterButtonActive : {})
                }}
              >
                {filtro.label}
              </button>
            ))}
          </div>

          <label style={styles.searchLabel}>
            Buscar docente
            <div style={styles.searchBox}>
              <input
                type="search"
                value={busquedaDocente}
                onChange={(event) => setBusquedaDocente(event.target.value)}
                placeholder="Nombre o apellido"
                style={styles.searchInput}
              />
              {busquedaDocente && (
                <button
                  type="button"
                  onClick={() => setBusquedaDocente("")}
                  style={styles.clearButton}
                  aria-label="Limpiar busqueda"
                >
                  Limpiar
                </button>
              )}

              {busquedaNormalizada && (
                <div style={styles.suggestions}>
                  {recomendaciones.length > 0 ? (
                    recomendaciones.map((docente) => (
                      <button
                        key={docente.nombre}
                        type="button"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          seleccionarDocenteDesdeBusqueda(docente.nombre);
                        }}
                        style={styles.suggestionItem}
                      >
                        <span style={styles.suggestionName}>{docente.nombre}</span>
                        <span style={styles.suggestionMeta}>
                          Tut. {docente.director} - Eval. {docente.evaluador}
                        </span>
                      </button>
                    ))
                  ) : (
                    <div style={styles.emptySuggestion}>No se encontraron docentes con ese nombre.</div>
                  )}
                </div>
              )}
            </div>
          </label>
        </div>
      </div>

      <div style={styles.grid}>
        {docentesFiltrados.map((d, i) => (
          <DocenteCard
            key={i}
            docente={d}
            onClick={() => abrirDetalleDocente(d.nombre)}
          />
        ))}
      </div>
    </div>
  );
}

export default Avances;

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: "24px"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "16px"
  },
  title: {
    margin: 0,
    color: theme.colors.text
  },
  subtitle: {
    margin: "8px 0 0",
    color: theme.colors.subtext,
    maxWidth: "720px",
    lineHeight: 1.5
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "16px"
  },
  controls: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "12px"
  },
  selectorLabel: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    color: theme.colors.subtext,
    fontSize: "14px",
    fontWeight: 700,
    minWidth: "160px"
  },
  select: {
    background: "#f8fbff",
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "10px",
    padding: "12px 14px",
    color: theme.colors.text,
    outline: "none"
  },
  filterPanel: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    justifyContent: "flex-end"
  },
  filterButton: {
    background: theme.colors.panelBg,
    color: theme.colors.text,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "999px",
    padding: "10px 14px",
    cursor: "pointer",
    fontWeight: 700
  },
  filterButtonActive: {
    background: theme.colors.primary,
    color: "#fff",
    border: `1px solid ${theme.colors.primary}`,
    boxShadow: "0 10px 20px rgba(19, 59, 104, 0.18)"
  },
  searchLabel: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    color: theme.colors.subtext,
    fontSize: "14px",
    fontWeight: 700,
    width: "min(360px, 100%)"
  },
  searchBox: {
    position: "relative",
    width: "100%"
  },
  searchInput: {
    width: "100%",
    background: "#f8fbff",
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "10px",
    color: theme.colors.text,
    outline: "none",
    padding: "12px 82px 12px 14px",
    fontSize: "14px"
  },
  clearButton: {
    position: "absolute",
    top: "50%",
    right: "8px",
    transform: "translateY(-50%)",
    background: "transparent",
    border: "none",
    color: theme.colors.primary,
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: 800,
    padding: "6px"
  },
  suggestions: {
    position: "absolute",
    top: "calc(100% + 8px)",
    left: 0,
    right: 0,
    zIndex: 20,
    background: "#fff",
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "12px",
    boxShadow: "0 18px 36px rgba(15, 23, 42, 0.14)",
    overflow: "hidden"
  },
  suggestionItem: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    background: "#fff",
    border: "none",
    borderBottom: `1px solid ${theme.colors.border}`,
    color: theme.colors.text,
    cursor: "pointer",
    padding: "12px 14px",
    textAlign: "left"
  },
  suggestionName: {
    flex: 1,
    minWidth: 0,
    fontSize: "13px",
    fontWeight: 800,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  suggestionMeta: {
    flexShrink: 0,
    color: theme.colors.subtext,
    fontSize: "12px",
    fontWeight: 700
  },
  emptySuggestion: {
    padding: "12px 14px",
    color: theme.colors.subtext,
    fontSize: "13px",
    fontWeight: 600
  }
};
